import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import api from '../services/api';
import { languageAlphabets, getCharactersForLanguage, universalNumbers } from '../utils/alphabets';
import './History.css';

const History = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [matrices, setMatrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    // Attendre un peu pour s'assurer que le token est disponible
    const timer = setTimeout(() => {
      loadMatrices();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Fermer le menu quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadMatrices = async () => {
    try {
      const response = await api.get('/matrices/');
      setMatrices(response.data.results || response.data);
    } catch (error) {
      console.error('Error loading matrices:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleMenu = (matriceId) => {
    setOpenMenuId(openMenuId === matriceId ? null : matriceId);
  };

  const handleEdit = (matriceId) => {
    setOpenMenuId(null);
    navigate(`/matrice/edit/${matriceId}`);
  };

  const handleDelete = async (matriceId) => {
    setOpenMenuId(null);
    
    if (!window.confirm(t('history.confirmDelete') || 'Êtes-vous sûr de vouloir supprimer cette matrice ?')) {
      return;
    }

    setDeleting(matriceId);
    try {
      await api.delete(`/matrices/${matriceId}/`);
      setMatrices(matrices.filter(m => m.id !== matriceId));
    } catch (error) {
      console.error('Error deleting matrix:', error);
      alert(t('history.deleteError') || 'Erreur lors de la suppression');
    } finally {
      setDeleting(null);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  if (loading) {
    return <div className="history-loading">Chargement...</div>;
  }

  return (
    <div className="history">
      <Navbar />

      <div className="history-content">
        {matrices.length === 0 ? (
          <div className="no-matrices">
            <p>Aucune matrice trouvée. Créez votre première matrice !</p>
          </div>
        ) : (
          <div className="matrices-list">
            {matrices.map((matrice) => (
              <div key={matrice.id} className="matrice-card">
                <div className="matrice-header">
                  <div className="matrice-header-left">
                    <h3>Matrice #{matrice.id}</h3>
                    {matrice.langue && languageAlphabets[matrice.langue] && (
                      <span className="matrice-language">
                        {languageAlphabets[matrice.langue].name}
                      </span>
                    )}
                  </div>
                  <span className="matrice-date">
                    {formatDate(matrice.date_creation)}
                  </span>
                </div>
                <div className="matrice-preview">
                  <div className="preview-mosaic">
                    {/* Afficher toutes les lettres de l'alphabet de la langue */}
                    {(() => {
                      const langue = matrice.langue || 'fr';
                      const characters = getCharactersForLanguage(langue);
                      const allLetters = characters.letters;
                      
                      return allLetters.map((lettre) => {
                        const couleur = matrice.lettres_couleurs?.[lettre];
                        return (
                          <div
                            key={`letter-${lettre}`}
                            className={`mosaic-tile ${!couleur ? 'mosaic-tile-empty' : ''}`}
                            style={couleur ? {
                              backgroundColor: couleur,
                            } : {}}
                            title={couleur ? `${lettre}: ${couleur}` : `${lettre}: non défini`}
                          />
                        );
                      });
                    })()}
                    {/* Afficher tous les chiffres (0-9) */}
                    {universalNumbers.map((chiffre) => {
                      const couleur = matrice.chiffres_couleurs?.[chiffre];
                      return (
                        <div
                          key={`number-${chiffre}`}
                          className={`mosaic-tile ${!couleur ? 'mosaic-tile-empty' : ''}`}
                          style={couleur ? {
                            backgroundColor: couleur,
                          } : {}}
                          title={couleur ? `${chiffre}: ${couleur}` : `${chiffre}: non défini`}
                        />
                      );
                    })}
                  </div>
                </div>
                <div className="matrice-stats">
                  <span>
                    {Object.keys(matrice.lettres_couleurs || {}).length} lettres
                  </span>
                  <span>
                    {Object.keys(matrice.chiffres_couleurs || {}).length} chiffres
                  </span>
                </div>
                
                {/* Menu kebab (trois points) */}
                <div className="matrice-menu" ref={openMenuId === matrice.id ? menuRef : null}>
                  <button
                    className="matrice-menu-button"
                    onClick={() => toggleMenu(matrice.id)}
                    title="Menu"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="1"/>
                      <circle cx="5" cy="12" r="1"/>
                      <circle cx="19" cy="12" r="1"/>
                    </svg>
                  </button>
                  
                  {openMenuId === matrice.id && (
                    <div className="matrice-menu-dropdown">
                      <button
                        className="matrice-menu-option matrice-menu-edit"
                        onClick={() => handleEdit(matrice.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        <span>{t('history.edit') || 'Modifier'}</span>
                      </button>
                      <button
                        className="matrice-menu-option matrice-menu-delete"
                        onClick={() => handleDelete(matrice.id)}
                        disabled={deleting === matrice.id}
                      >
                        {deleting === matrice.id ? (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="spinner">
                              <circle cx="12" cy="12" r="10"/>
                            </svg>
                            <span>{t('history.deleting') || 'Suppression...'}</span>
                          </>
                        ) : (
                          <>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                              <line x1="10" y1="11" x2="10" y2="17"/>
                              <line x1="14" y1="11" x2="14" y2="17"/>
                            </svg>
                            <span>{t('history.delete') || 'Supprimer'}</span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

function getContrastColor(hexColor) {
  if (!hexColor || hexColor === '#ffffff') return '#000000';
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 128 ? '#000000' : '#ffffff';
}

export default History;

